import type { PremiseSummary } from "../http/foyer";
import { productsForPremise } from "./products";

/**
 * A customer's Overview (Sigge, 2026-09-29).
 *
 * A customer session gets ONE "Overview" entry in the left menu, and the
 * header's product selector switches what it shows between the two products
 * that have an overview page:
 *
 *  - Frontlayer -> the location dashboard (`/places/overview`)
 *  - Signage    -> the screens overview (`/signage-overview`)
 *
 * The selector offers only the ones the customer actually has anywhere - a
 * signage-only customer sees Signage and nothing else, and the same for
 * Frontlayer. Nenda staff keep the ordinary "All products" filter.
 */
export type OverviewProduct = "frontlayer" | "signage";

/** Selector order. */
const OVERVIEW_PRODUCTS: OverviewProduct[] = ["frontlayer", "signage"];

export const OVERVIEW_PATHS: Record<OverviewProduct, string> = {
  frontlayer: "/places/overview",
  signage: "/signage-overview",
};

/** The route a customer lands on; it resolves to the right overview page. */
export const CUSTOMER_OVERVIEW_ROUTE = "/overview";

/** The overview products held at any of these places, in selector order. */
export function overviewProductsFor(
  premises: Pick<PremiseSummary, "products">[]
): OverviewProduct[] {
  const held = new Set<string>();
  for (const p of premises) {
    for (const key of productsForPremise(p)) held.add(key);
  }
  return OVERVIEW_PRODUCTS.filter((k) => held.has(k));
}

/**
 * The product whose overview is showing: the selected one when the customer
 * has it, else Signage (their landing page since 2026-09-28), else Frontlayer.
 * Null when they have neither.
 */
export function effectiveOverviewProduct(
  selected: string,
  available: OverviewProduct[]
): OverviewProduct | null {
  if (available.includes(selected as OverviewProduct)) {
    return selected as OverviewProduct;
  }
  if (available.includes("signage")) return "signage";
  return available[0] ?? null;
}

/** True on either overview page - the one menu entry is active on both. */
export function isOverviewPath(pathname: string): boolean {
  return Object.values(OVERVIEW_PATHS).some((p) => pathname.startsWith(p));
}
